import { useEffect, useRef, useState } from "react";
import { AlertCircle, ChevronRight, Loader2, X } from "lucide-react";
import { format } from "date-fns";
import toast from "react-hot-toast";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "@/utils/cn";
import { usePermissions } from "@/hooks/usePermissions";
import type { ProgramDetailDto } from "@/types/grc";
import {
  softHoldKeys,
  useSoftHoldsByMonth,
  useUpdatePreferredDateSelectionStatus
} from "@/services/softHolds";
import { grcKeys } from "@/services/grc";
import type { SoftHoldPreferredDate } from "@/types/softHolds";

interface SoftHoldsFlyoutProps {
  month: number;
  year: number;
  initialSoftHoldId: number;

  onSoftHoldItemClick?: (softHoldId: number, isDraftEvent: boolean) => void;

  /** Called after a preferred-date checkbox is toggled so the GRC grid can reload. */
  onPreferredDateChanged?: () => void;
  onClose: () => void;
}

export function SoftHoldsFlyout({
  month,
  year,
  initialSoftHoldId,


  onSoftHoldItemClick,

  onPreferredDateChanged,
  onClose
}: SoftHoldsFlyoutProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canEditSoftHold = hasPermission("CanEditSoftHold");

  const [selectedSoftHoldId, setSelectedSoftHoldId] = useState(initialSoftHoldId);
  const hasValidMonthYear = month > 0 && year > 0;

  const { data: softHolds, isPending: isListPending } = useSoftHoldsByMonth(month, year);

  const updatePreferredDate = useUpdatePreferredDateSelectionStatus();

  useEffect(() => {
    const firstId = softHolds?.[0]?.id ?? 0;
    if (selectedSoftHoldId <= 0 && firstId > 0) setSelectedSoftHoldId(firstId);
  }, [softHolds, selectedSoftHoldId]);

  useEffect(() => {
    setSelectedSoftHoldId(initialSoftHoldId);
  }, [initialSoftHoldId]);

  useEffect(() => {
    const firstFocusable = panelRef.current?.querySelector<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    firstFocusable?.focus();
  }, []);

  function handlePanelKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (e.key === "Escape") {
      onClose();
      return;
    }
    if (e.key !== "Tab") return;

    const focusables = Array.from(
      panelRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) ?? []
    );
    if (focusables.length === 0) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  async function handlePreferredDateToggle(
    softHoldId: number,
    preferredDateId: number,
    isSelected: boolean
  ) {
    if (!canEditSoftHold) return;

    try {
      await updatePreferredDate.mutateAsync({
        softHoldId,
        preferredDateId,
        isSelected
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: softHoldKeys.byMonth(month, year) }),
        queryClient.invalidateQueries({ queryKey: softHoldKeys.byId(softHoldId) }),
        // Reload the GRC grid so preferred-date cell highlights update
        queryClient.invalidateQueries({ queryKey: grcKeys.byMonth(month, year) })
      ]);
      onPreferredDateChanged?.();
    } catch {
      toast.error("Unable to update preferred date selection.");
    }
  }

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/30" onClick={onClose} aria-hidden="true" />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="soft-holds-flyout-title"
        tabIndex={-1}
        className="fixed inset-y-0 right-0 z-50 ml-auto flex h-full w-[30vw] min-w-[280px] flex-col border-l border-slate-300 bg-white shadow-xl"
        onKeyDown={handlePanelKeyDown}
      >
        <div className="flex items-center justify-between bg-black px-4 py-3">
          <h2 id="soft-holds-flyout-title" className="text-base font-semibold text-white">
            Soft Holds
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close soft holds dialog"
            className="text-white hover:text-slate-300"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {/* Scrollable list — matches viewsoftholds.css .table-container */}
        <div className="flex-1 overflow-y-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          {hasValidMonthYear && isListPending ? (
            <LoadingState message="Loading soft holds..." />
          ) : (
            <div>
              {/* Empty state – only when both lists have no items */}
              {(!softHolds || softHolds.length === 0) && (
                  <p className="px-4 py-6 text-sm text-slate-500">
                    No soft holds found for this month.
                  </p>
                )}

              {/* ── Soft hold rows ── */}
              {(softHolds ?? []).map((softHold, idx) => (
                <div
                  key={softHold.id}
                  className={cn(
                    "flex items-center justify-between",
                    idx % 2 === 0 ? "bg-white" : "bg-[#F2F2F2]"
                  )}
                  style={{ paddingTop: 15, paddingBottom: 15, paddingLeft: 15,paddingRight: 15 }}
                >
                  <div className="min-w-0 flex-1">
                    {/* Meeting ID | Name – ID in fixed 50 px span */}
                    <div className="mb-1 text-sm font-semibold text-slate-900">
                      <span style={{ display: "inline-block", minWidth: 50 }}>
                        {softHold.meetingIdDisplay || "------"}
                      </span>
                      <span style={{ marginLeft: 8, marginRight: 8 }}>|</span>
                      <span>{softHold.meetingName}</span>
                    </div>

                    {/* Preferred date rows */}
                    {softHold.preferredDates.map((date) => (
                      <div
                        key={date.id}
                        className="flex items-center gap-1.5"
                        style={{ marginTop: 5, marginBottom: 5 }}
                      >
                        <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 text-amber-500" />
                        <input
                          type="checkbox"
                          checked={date.isSelected}
                          onChange={(e) =>
                            void handlePreferredDateToggle(softHold.id, date.id, e.target.checked)
                          }
                          disabled={!canEditSoftHold || updatePreferredDate.isPending}
                          className="h-4 w-4 flex-shrink-0 cursor-pointer rounded-none border border-slate-400 accent-black"
                          aria-label={`Select preferred date ${formatDateRange(date)}`}
                        />
                        <span
                          className="text-black"
                          style={{ fontSize: 13, fontWeight: 400 }}
                        >
                          {formatDateRange(date)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Transparent chevron button – matches .arrowcss */}
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedSoftHoldId(softHold.id);
                      onSoftHoldItemClick?.(softHold.id, softHold.isDraftEvent ?? false);
                    }}
                    aria-label={`View details for ${softHold.meetingName}`}
                    className="flex-shrink-0 self-center p-1 text-slate-600"
                    style={{ background: "transparent", border: "none", boxShadow: "none" }}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              ))}

              {/* ── Draft calendar rows ── */}
              {/* {includeDraftItems &&
                draftItems.map((draftItem, di) => {
                  const overallIdx = (softHolds?.length ?? 0) + di;
                  const dateRanges = getDraftDateRanges(draftItem);
                  return (
                    <div
                      key={`draft-${draftItem.softHoldId}`}
                      className={cn(
                        "flex items-center justify-between",
                        overallIdx % 2 === 0 ? "bg-white" : "bg-[#F2F2F2]"
                      )}
                      style={{ paddingTop: 10, paddingBottom: 10, paddingLeft: 10 }}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="mb-1 text-sm font-semibold text-slate-900">
                          <span style={{ display: "inline-block", minWidth: 50 }}>
                            {draftItem.eventId || "------"}
                          </span>
                          <span style={{ marginLeft: 8, marginRight: 8 }}>|</span>
                          <span>{draftItem.eventName}</span>
                        </div>

                        {dateRanges.map((dr, i) => (
                          <div
                            key={i}
                            className="flex items-center gap-1.5"
                            style={{ marginTop: 5, marginBottom: 5 }}
                          >
                            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 text-amber-500" />
                            <span
                              className="text-black"
                              style={{ fontSize: 13, fontWeight: 400 }}
                            >
                              {toDateDisplay(dr.startDate)} - {toDateDisplay(dr.endDate)}
                            </span>
                          </div>
                        ))}
                      </div>

                      <button
                        type="button"
                        onClick={() => onDraftItemClick?.(draftItem.softHoldId)}
                        aria-label={`View details for ${draftItem.eventName}`}
                        className="flex-shrink-0 self-start p-1 text-slate-600"
                        style={{ background: "transparent", border: "none", boxShadow: "none" }}
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  );
                })} */}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3">
          <span className="text-sm font-semibold text-slate-700">
            Total Add:{" "}
            <strong>
              {(softHolds?.length ?? 0) }
            </strong>
          </span>

          <button
            type="button"
            onClick={onClose}
            className="rounded-none border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            Close
          </button>
        </div>
      </div>
    </>
  );
}

function LoadingState({ message }: { message: string }) {
  return (
    <div className="flex items-center justify-center py-10 text-sm text-slate-500">
      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
      {message}
    </div>
  );
}

function formatDateRange(date: SoftHoldPreferredDate) {
  return `${toDateDisplay(date.startDate)} - ${toDateDisplay(date.endDate)}`;
}

function toDateDisplay(value: string | null | undefined) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return format(date, "MM-dd-yyyy");
}

/**
 * Computes preferred date ranges from a draft ProgramDetailDto's roomBlockDtos,
 * mirroring the Blazor LINQ: group by PrefereBlockId, min/max StartDate.
 */
function getDraftDateRanges(
  prog: ProgramDetailDto
): { startDate: string; endDate: string }[] {
  const groups: Record<number, string[]> = {};
  for (const block of Object.values(prog.roomBlockDtos)) {
    if (block.prefereBlockId !== 0 && block.blocked > 0) {
      (groups[block.prefereBlockId] ??= []).push(block.startDate);
    }
  }
  return Object.values(groups).map((dates) => ({
    startDate: dates.reduce((a, b) => (a < b ? a : b)),
    endDate: dates.reduce((a, b) => (a > b ? a : b))
  }));
}
